import {t} from 'i18next';
import jsPDF from 'jspdf';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type {CustomMenuItem} from '../../../common/arr-menu/ArrMenu';

dayjs.extend(utc);
import {formatDateTime, numberFormatter} from '../../../../utils/utils';
import {IGetAIReviewJobDetailsResponse} from '../../../../api/types/aiReview.type';

export enum AIReviewJobStatus {
  InProgress = 'in Progress',
  Complete = 'complete',
  Failed = 'failed',
  Queued = 'queued',
  Draft = 'draft',
}

export const tableProps = {
  ROW_HEIGHT: 36,
  FILTER_HEADER_HEIGHT: 48,
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
};

const status = [
  AIReviewJobStatus.InProgress,
  AIReviewJobStatus.Complete,
  AIReviewJobStatus.Failed,
  AIReviewJobStatus.Queued,
  AIReviewJobStatus.Draft,
];
const defaultTextClass: Record<AIReviewJobStatus, string> = Object.fromEntries(
  status.map((item) => [item, 'text-headings'])
) as Record<AIReviewJobStatus, string>;

const defaultNumClass: Record<AIReviewJobStatus, string> = Object.fromEntries(
  status.map((item) => [
    item,
    item === 'draft' ? 'text-body-subtle' : 'text-action-default',
  ])
) as Record<AIReviewJobStatus, string>;

export interface IColumnsData {
  field: string;
  headerName: string;
  fieldDataKey: string;
  filterable: boolean;
  width: number;
  textColorClass: {[key in AIReviewJobStatus]: string};
  align?: string;
  clickable?: {[key in AIReviewJobStatus]: boolean};
  sortable?: boolean;
  formatFunction?: (...args: string[]) => void;
}

function formatNumber(text: string, status: string) {
  const formattedText = text ? numberFormatter.format(Number(text)) : text;
  return status?.toLowerCase() === AIReviewJobStatus.Draft &&
    Number(formattedText) === 0
    ? '---'
    : formattedText;
}
function formatDate(dateString: string) {
  return dateString ? formatDateTime(dateString) : dateString;
}

function formatStatus(status: string) {
  return status.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
}

enum AiReviewJobType {
  ISSUECODE = 'Issue Coding',
  RELEVANCY = 'Relevancy',
}
function jobTypeMapping(jobType: string): string {
  return AiReviewJobType?.[jobType as keyof typeof AiReviewJobType] ?? jobType;
}
export const columnsData: IColumnsData[] = [
  {
    field: 'ReviewJobID',
    headerName: t('ai.review.jobList.jobListColumns.rowId'),
    fieldDataKey: 'jobId',
    filterable: false,
    width: 70,
    textColorClass: defaultTextClass,
    align: 'center',
  },
  {
    field: 'JobName',
    headerName: t('ai.review.jobList.jobListColumns.jobName'),
    fieldDataKey: 'jobName',
    filterable: true,
    width: 260,
    textColorClass: {
      ...defaultTextClass,
      [AIReviewJobStatus.Complete]: 'text-action-default',
      [AIReviewJobStatus.Draft]: 'text-action-default',
    },
    clickable: {
      [AIReviewJobStatus.InProgress]: false,
      [AIReviewJobStatus.Complete]: true,
      [AIReviewJobStatus.Failed]: false,
      [AIReviewJobStatus.Queued]: false,
      [AIReviewJobStatus.Draft]: true,
    },
  },
  {
    field: 'JobType',
    headerName: t('ai.review.jobList.jobListColumns.type'),
    fieldDataKey: 'jobType',
    filterable: true,
    width: 140,
    textColorClass: defaultTextClass,
    formatFunction: jobTypeMapping,
  },
  {
    field: 'CreatedOn',
    headerName: t('ai.review.jobList.jobListColumns.createdDate'),
    fieldDataKey: 'createdDate',
    filterable: false,
    width: 180,
    textColorClass: defaultTextClass,
    formatFunction: formatDate,
  },
  {
    field: 'ExecutedDate',
    headerName: t('ai.review.jobList.jobListColumns.executedDate'),
    fieldDataKey: 'executionDate',
    filterable: false,
    width: 180,
    textColorClass: defaultTextClass,
    formatFunction: formatDate,
  },
  {
    field: 'CreatedBy',
    headerName: t('ai.review.jobList.jobListColumns.createdBy'),
    fieldDataKey: 'createdBy',
    filterable: true,
    width: 160,
    textColorClass: defaultTextClass,
  },
  {
    field: 'Source',
    headerName: t('ai.review.jobList.jobListColumns.source'),
    fieldDataKey: 'source',
    filterable: true,
    width: 190,
    textColorClass: defaultTextClass,
  },
  {
    field: 'totalDocCount',
    headerName: t('ai.review.jobList.jobListColumns.totalDocs'),
    fieldDataKey: 'totalDocCount',
    filterable: false,
    sortable: false,
    width: 107,
    textColorClass: defaultNumClass,
    formatFunction: formatNumber,
  },
  {
    field: 'completedDocCount',
    headerName: t('ai.review.jobList.jobListColumns.completed'),
    fieldDataKey: 'completedDocCount',
    filterable: false,
    sortable: false,
    width: 127,
    textColorClass: {
      ...defaultNumClass,
      [AIReviewJobStatus.Failed]: 'text-headings',
    },
    formatFunction: formatNumber,
  },
  {
    field: 'excludedErrorDocCount',
    headerName: t('ai.review.jobList.jobListColumns.excludedError'),
    fieldDataKey: 'excludedErrorDocCount',
    filterable: false,
    sortable: false,
    width: 150,
    textColorClass: {
      ...defaultNumClass,
      [AIReviewJobStatus.Failed]: 'text-critical',
    },
    formatFunction: formatNumber,
  },
  {
    field: 'Status',
    headerName: t('ai.review.jobList.jobListColumns.status'),
    fieldDataKey: 'status',
    filterable: true,
    width: 120,
    formatFunction: formatStatus,
    textColorClass: {
      ...defaultTextClass,
      [AIReviewJobStatus.InProgress]: 'text-notice',
      [AIReviewJobStatus.Complete]: 'text-success',
      [AIReviewJobStatus.Failed]: 'text-critical',
      [AIReviewJobStatus.Draft]: 'text-body-subtle',
    },
  },
];

const KNOWN_JOB_STATUSES = new Set<string>(Object.values(AIReviewJobStatus));

const STATUS_ALIAS_MAP = new Map<string, AIReviewJobStatus>([
  ['in progress', AIReviewJobStatus.InProgress],
  ['inprogress', AIReviewJobStatus.InProgress],
  ['in-progress', AIReviewJobStatus.InProgress],
  ['in_progress', AIReviewJobStatus.InProgress],
  ['draft', AIReviewJobStatus.Draft],
  ['complete', AIReviewJobStatus.Complete],
  ['completed', AIReviewJobStatus.Complete],
  ['failed', AIReviewJobStatus.Failed],
  ['queued', AIReviewJobStatus.Queued],
]);

/**
 * Maps grid/API job status values to {@link AIReviewJobStatus}.
 * Returns null when the value cannot be matched (empty menu for that row).
 */
export const parseJobListRowStatus = (
  raw: unknown
): AIReviewJobStatus | null => {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (KNOWN_JOB_STATUSES.has(s)) {
    return s as AIReviewJobStatus;
  }
  const norm = s.toLowerCase().replace(/\s+/g, ' ');
  return STATUS_ALIAS_MAP.get(norm) ?? null;
};

export const getJobListMenuItems = (
  status: AIReviewJobStatus | null
): CustomMenuItem[] => {
  if (status === AIReviewJobStatus.InProgress) {
    return [
      {id: 'addToGroup', label: '', value: 'addToGroup'},
      {id: 'removeFromGroup', label: '', value: 'removeFromGroup'},
      {id: 'divider', label: '', value: 'divider', variant: 'divider'},
      {id: 'deleteJob', label: '', value: 'deleteJob'},
    ];
  }

  if (status === AIReviewJobStatus.Failed) {
    return [
      {id: 'retry', label: '', value: 'retry'},
      {id: 'cloneJob', label: '', value: 'cloneJob'},
      {
        id: 'exportJobDetailsAsPdf',
        label: '',
        value: 'exportJobDetailsAsPdf',
      },
      {id: 'divider-1', label: '', value: 'divider-1', variant: 'divider'},
      {id: 'addToGroup', label: '', value: 'addToGroup'},
      {id: 'removeFromGroup', label: '', value: 'removeFromGroup'},
      {id: 'divider-2', label: '', value: 'divider-2', variant: 'divider'},
      {id: 'deleteJob', label: '', value: 'deleteJob'},
    ];
  }

  if (status === AIReviewJobStatus.Draft) {
    return [
      {id: 'edit', label: '', value: 'edit'},
      {id: 'cloneJob', label: '', value: 'cloneJob'},
      {
        id: 'exportJobDetailsAsPdf',
        label: '',
        value: 'exportJobDetailsAsPdf',
      },
      {id: 'divider-1', label: '', value: 'divider-1', variant: 'divider'},
      {id: 'addToGroup', label: '', value: 'addToGroup'},
      {id: 'removeFromGroup', label: '', value: 'removeFromGroup'},
      {id: 'divider-2', label: '', value: 'divider-2', variant: 'divider'},
      {id: 'deleteJob', label: '', value: 'deleteJob'},
    ];
  }

  if (status === AIReviewJobStatus.Complete) {
    return [
      {id: 'cloneJob', label: '', value: 'cloneJob'},
      {
        id: 'exportJobDetailsAsPdf',
        label: '',
        value: 'exportJobDetailsAsPdf',
      },
      {id: 'divider-1', label: '', value: 'divider-1', variant: 'divider'},
      {id: 'addToGroup', label: '', value: 'addToGroup'},
      {id: 'removeFromGroup', label: '', value: 'removeFromGroup'},
      {id: 'divider-2', label: '', value: 'divider-2', variant: 'divider'},
      {id: 'deleteJob', label: '', value: 'deleteJob'},
    ];
  }

  return [];
};

const sanitizeMultilineText = (text?: string): string =>
  text
    ? text
        .replace(/[\r\n\t]+/g, ' ') // remove newlines, carriage returns, tabs
        .replace(/\s+/g, ' ') // normalize multiple spaces to one
        .trim() // remove leading/trailing spaces
    : '';

//Format ISO date to "DD-MM-YYYY HH:MM:SS" in UTC
export const formatDateTimeUTC = (iso?: string): string => {
  if (!iso) return '';

  const d = dayjs.utc(iso);
  if (!d.isValid()) return '';

  return d.format('DD-MM-YYYY HH:mm:ss');
};

// Render a label/value pair with proper wrapping.
function drawLabelValue(
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
): number {
  const labelText = `${label}: `;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.text(labelText, x, y);

  const labelWidth = doc.getTextWidth(labelText);

  const wrapped = doc.splitTextToSize(
    value || '',
    Math.max(10, maxWidth - labelWidth)
  );

  if (wrapped.length === 0) return y;

  doc.text(wrapped[0], x + labelWidth, y);

  let cursorY = y;
  for (let i = 1; i < wrapped.length; i++) {
    cursorY += lineHeight;
    doc.text(wrapped[i], x + labelWidth, cursorY);
  }

  return cursorY;
}

//Ensures enough vertical space exists.
// Adds new page if required and returns updated Y position.
function ensureSpace(
  doc: jsPDF,
  needed: number,
  currentY: number,
  margin: number
): number {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + needed > pageHeight - margin) {
    doc.addPage();
    return margin;
  }
  return currentY;
}

export function exportAIReviewJobDetailsPdf(
  jobData: IGetAIReviewJobDetailsResponse
): void {
  const doc = new jsPDF();

  const margin = 15;
  const lineHeight = 7;
  const contentWidth = doc.internal.pageSize.getWidth() - margin * 2;

  let y = margin;

  //Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(jobData.jobName || 'Job Name', margin, 18);

  //Meta
  const createdAt = formatDateTimeUTC(jobData.createdAt);
  const createdByName =
    [jobData.firstName, jobData.lastName].filter(Boolean).join(' ') || '---';
  const idStr = `${jobData.jobId ?? ''}`;
  const jobTypeLabel =
    jobData.jobType === 'ISSUECODE'
      ? 'Issue Coding'
      : jobData.jobType === 'RELEVANCY'
        ? 'Relevancy'
        : jobData.jobType || '';

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  y += lineHeight * 1.5;

  //Date
  y = ensureSpace(doc, 14, y, margin);
  doc.text(createdAt, margin, y);
  y += 14;

  //Fields
  y = ensureSpace(doc, lineHeight, y, margin);
  y = drawLabelValue(doc, 'Job ID', idStr, margin, y, contentWidth, lineHeight);
  y += lineHeight;

  y = ensureSpace(doc, lineHeight, y, margin);
  y = drawLabelValue(
    doc,
    'Type',
    jobTypeLabel,
    margin,
    y,
    contentWidth,
    lineHeight
  );
  y += lineHeight;

  y = ensureSpace(doc, lineHeight, y, margin);
  y = drawLabelValue(
    doc,
    'Created By',
    createdByName || '',
    margin,
    y,
    contentWidth,
    lineHeight
  );
  y += lineHeight;

  y = ensureSpace(doc, lineHeight, y, margin);
  y = drawLabelValue(
    doc,
    'Status',
    jobData.status || '',
    margin,
    y,
    contentWidth,
    lineHeight
  );
  y += lineHeight;

  y = ensureSpace(doc, lineHeight, y, margin);
  y = drawLabelValue(
    doc,
    'Selected Source',
    jobData.sourceType || '',
    margin,
    y,
    contentWidth,
    lineHeight
  );

  // case context
  if (jobData.caseContext) {
    y += 14;
    y = ensureSpace(doc, lineHeight, y, margin);

    doc.setFont('helvetica', 'bold');
    doc.text('Case Context:', margin, y);

    y += lineHeight;

    doc.setFont('helvetica', 'normal');

    const caseContextText = sanitizeMultilineText(jobData.caseContext);

    for (const line of doc.splitTextToSize(caseContextText, contentWidth)) {
      y = ensureSpace(doc, lineHeight, y, margin);
      doc.text(line, margin, y);
      y += lineHeight;
    }
  }

  // Issue Coding
  if (jobTypeLabel === 'Issue Coding') {
    y = ensureSpace(doc, lineHeight, y, margin);

    doc.setFont('helvetica', 'bold');
    doc.text('Issue Codes:', margin, y);
    y += lineHeight;

    doc.setFont('helvetica', 'normal');
    const issues = jobData.issueCodes ?? [];

    if (issues.length === 0) {
      doc.text('None', margin, y);
    } else {
      for (const item of issues) {
        const rawText = `${item.code || ''}: ${item.question || ''}`.trim();
        const issueText = sanitizeMultilineText(rawText);

        for (const line of doc.splitTextToSize(issueText, contentWidth)) {
          y = ensureSpace(doc, lineHeight, y, margin);
          doc.text(line, margin, y);
          y += lineHeight;
        }
      }
    }
  }

  // Relevancy
  if (jobTypeLabel === 'Relevancy' && jobData.relevanceCriteria) {
    y += lineHeight;
    y = ensureSpace(doc, lineHeight, y, margin);

    doc.setFont('helvetica', 'bold');
    doc.text('Relevance Criteria:', margin, y);
    y += lineHeight;

    doc.setFont('helvetica', 'normal');

    const criteriaText = sanitizeMultilineText(jobData.relevanceCriteria);

    for (const line of doc.splitTextToSize(criteriaText, contentWidth)) {
      y = ensureSpace(doc, lineHeight, y, margin);
      doc.text(line, margin, y);
      y += lineHeight;
    }
  }

  doc.save(`AIReviewJobDetails_${idStr}.pdf`);
}
